import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, INITIAL_STATE } from '../utils/constants';
import { getMedianPrice } from '../utils/formatters';
import { fetchWithAbort } from '../utils/fetchWithAbort';

export function useCantonData() {
  const [data, setData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    // Fetch all data in parallel via our backend API
    const [roundRes, rewardsRes, validatorsRes, dsoRes, priceVotesRes, txRes, supplyRes] = await Promise.all([
      fetchWithAbort(`${API_BASE}/round`, {}, signal),
      fetchWithAbort(`${API_BASE}/rewards`, {}, signal),
      fetchWithAbort(`${API_BASE}/validators/all`, {}, signal),
      fetchWithAbort(`${API_BASE}/dso`, {}, signal),
      fetchWithAbort(`${API_BASE}/price-votes`, {}, signal),
      fetchWithAbort(`${API_BASE}/transactions`, {
        method: 'POST',
        body: JSON.stringify({ page_size: 20, sort_order: 'desc' }),
      }, signal),
      fetchWithAbort(`${API_BASE}/total-supply`, {}, signal),
    ]);

    // Check if we were aborted or unmounted
    if (signal.aborted || !isMountedRef.current) {
      return;
    }

    // Process validators data (all pages already fetched by backend)
    let validatorsList = null;
    if (validatorsRes.ok && validatorsRes.data) {
      const allLicenses = validatorsRes.data.validator_licenses || [];
      validatorsList = allLicenses.map(v => ({
        party: v.payload?.validator || v.contract_id,
        name: v.payload?.metadata?.contactPoint?.split('@')[0] || 'Validator',
        contactPoint: v.payload?.metadata?.contactPoint || '',
        status: 'active',
        version: v.payload?.metadata?.version || '',
        lastActiveAt: v.payload?.lastActiveAt,
      }));
    }

    // Process DSO data for SV nodes
    let dsoInfo = null;
    let svNodes = null;
    let openMiningRound = null;
    if (dsoRes.ok && dsoRes.data) {
      dsoInfo = dsoRes.data;

      // Extract SV info from dso_rules
      if (dsoInfo.dso_rules?.contract?.payload?.svs) {
        const svs = dsoInfo.dso_rules.contract.payload.svs;
        svNodes = svs.map(([partyId, info]) => ({
          party: partyId,
          name: info.name,
          weight: parseInt(info.svRewardWeight) || 0,
          joinedRound: info.joinedAsOfRound?.number || 0,
        })).sort((a, b) => b.weight - a.weight);
      }

      // Extract open mining round info
      if (dsoInfo.latest_mining_round?.contract?.payload) {
        openMiningRound = dsoInfo.latest_mining_round.contract.payload;
      }
    }

    // Process price votes
    let priceVotes = null;
    let ccPrice = null;
    if (priceVotesRes.ok && priceVotesRes.data) {
      priceVotes = priceVotesRes.data.amulet_price_votes || priceVotesRes.data || [];
      ccPrice = getMedianPrice(priceVotes);
    }

    // Process transactions
    let transactions = null;
    if (txRes.ok && txRes.data) {
      transactions = (txRes.data.transactions || []).slice(0, 15);
    }

    // Final check before updating state
    if (!isMountedRef.current) return;

    // Update state, preserving previous values for failed fetches
    setData(prev => ({
      networkStats: {
        totalRewardsCollected: rewardsRes.ok ? rewardsRes.data?.amount : prev.networkStats.totalRewardsCollected,
        currentRound: roundRes.ok ? roundRes.data?.round : prev.networkStats.currentRound,
        validatorCount: validatorsList !== null ? validatorsList.length : prev.networkStats.validatorCount,
        svCount: svNodes !== null ? svNodes.length : prev.networkStats.svCount,
        ccPrice: ccPrice ?? prev.networkStats.ccPrice ?? (openMiningRound?.amuletPrice ? parseFloat(openMiningRound.amuletPrice) : null),
        amuletPriceVotes: priceVotes !== null ? priceVotes : prev.networkStats.amuletPriceVotes,
        totalSupply: supplyRes.ok ? supplyRes.data?.totalSupply : prev.networkStats.totalSupply,
      },
      latestRound: roundRes.ok ? {
        round: roundRes.data?.round,
        effectiveAt: roundRes.data?.effectiveAt,
      } : prev.latestRound,
      openMiningRound: openMiningRound ?? prev.openMiningRound,
      validators: validatorsList !== null ? validatorsList : prev.validators,
      svNodes: svNodes !== null ? svNodes : prev.svNodes,
      transactions: transactions !== null ? transactions : prev.transactions,
      dsoInfo: dsoInfo ?? prev.dsoInfo,
    }));

    setLastUpdate(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      // Cancel any in-flight request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return { data, loading, error, lastUpdate, fetchData };
}
